import styled from 'styled-components/macro';
import { useApiRequest } from '../../../api/useApiRequest';
import { saveFile } from '../../../helpers/fileDownload';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { PageContainer } from '../../../layout/Layout';
import { PrimaryProgressButton } from '../../../shared/Button';
import { ErrorBox } from '../../../shared/ErrorBox';
import { GetRequest } from '../../../shared/GetRequest';
import { Header1 } from '../../../shared/Headers';
import { spacing05 } from '../../../styling/spacing';
import { HandoverPackResponse } from '../../shared/handover-pack/handoverPackResponse';
import { HandoverPackSectionDetails } from '../../shared/handover-pack/HandoverPackSectionDetails';

type Props = RouteComponentProps<{ projectId: string; designId: string }>;

export const ViewDesignHandoverPack = ({ projectId, designId }: Props) => {
  const getDesignHandoverPackRequest = useApiRequest<HandoverPackResponse>({
    httpMethod: 'get',
    endpointUrl: `projects/${projectId}/designs/${designId}/handover-pack`,
  });

  const {
    error: documentsToPrintError,
    inProgress: documentsToPrintInProgress,
    sendRequest: sendDocumentsToPrintRequest,
  } = useApiRequest<ArrayBuffer>({
    httpMethod: 'get',
    endpointUrl: `handover-pack/designs/${designId}/documents-to-print-csv`,
  });

  const {
    error: downloadAllDocumentsError,
    inProgress: downloadAllDocumentsInProgress,
    sendRequest: sendDownloadAllDocumentsRequest,
  } = useApiRequest<ArrayBuffer>({
    httpMethod: 'get',
    endpointUrl: `handover-pack/designs/${designId}/download-all-documents-csv`,
  });

  const {
    error: handoverPackError,
    inProgress: handoverPackInProgress,
    sendRequest: sendHandoverPackRequest,
  } = useApiRequest<ArrayBuffer>({
    httpMethod: 'get',
    endpointUrl: `handover-pack/designs/${designId}/summary`,
  });

  return (
    <GetRequest getDataRequest={getDesignHandoverPackRequest}>
      {({ response }) => {
        return (
          <PageContainer>
            <HeaderRow>
              <Header1>Handover Pack for Design</Header1>
              <ActionButtons>
                <PrimaryProgressButton
                  inProgress={downloadAllDocumentsInProgress}
                  onClick={async () => {
                    await sendDownloadAllDocumentsRequest({ onSuccess: saveFile });
                  }}
                >
                  Download All Documents CSV
                </PrimaryProgressButton>
                <PrimaryProgressButton
                  inProgress={documentsToPrintInProgress}
                  onClick={async () => {
                    await sendDocumentsToPrintRequest({
                      onSuccess: saveFile,
                    });
                  }}
                >
                  Download Printed Documents CSV
                </PrimaryProgressButton>
                <PrimaryProgressButton
                  inProgress={handoverPackInProgress}
                  onClick={async () => {
                    await sendHandoverPackRequest({
                      onSuccess: saveFile,
                    });
                  }}
                >
                  Download Handover Pack PDF
                </PrimaryProgressButton>
              </ActionButtons>
            </HeaderRow>
            {downloadAllDocumentsError != null && <ErrorBox error={downloadAllDocumentsError} />}
            {documentsToPrintError != null && <ErrorBox error={documentsToPrintError} />}
            {handoverPackError != null && <ErrorBox error={handoverPackError} />}
            {response.handoverPackDocumentSections.length === 0 ? (
              <p>No handover pack documents, please double check via Document Management page</p>
            ) : (
              response.handoverPackDocumentSections.map(section => (
                <HandoverPackSectionDetails section={section} key={section.handoverPackSectionId} />
              ))
            )}
          </PageContainer>
        );
      }}
    </GetRequest>
  );
};

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing05};
`;

const ActionButtons = styled.div`
  display: flex;
  column-gap: ${spacing05};
`;
